
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import ApplicantsDetails from "./ApplicantsDetails";
import { toast } from "react-toastify";
import Header from "../../Common/Layout/Header";
import { RiArrowLeftLine, RiVisaLine, RiFileList3Line } from "react-icons/ri";
import { useAuth } from "../../Common/Configurations/AuthContext";
import DefaultData from "../../CompanyData/DefaultData";

const RequestDetails = () => {
  const { visaRequestId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [visaRequest, setVisaRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDocuments, setShowDocuments] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  useEffect(() => {
    VisaRequest();
    
    // Check if documents were passed via navigation state
    if (location.state?.documents) {
      console.log("Documents received from Visas screen:", location.state.documents);
      setDocuments(location.state.documents);
    }
  }, [visaRequestId, location.state]);

  const VisaRequest = async () => {
    try {
      const res = await apiService.get(
        `${config.GETVISAREQUESTBYID}/${visaRequestId}`
      );
      setVisaRequest(res);
    } catch (error) {
      console.error("Error fetching visa request:", error);
    } finally {
      setLoading(false);
    }
  };

  // Updated pricing calculation with service fee from visa request
  const calculatePricing = () => {
    if (!visaRequest) return null;

    const supplierPrice = visaRequest.totalPrice - visaRequest.serviceFee || 0;
    // Use service fee from visa request (which came from the visa's b2BPrice.serviceFee)
    const serviceFee = visaRequest.serviceFee || 0;
    const markUP = visaRequest.markUP || 0;
    const totalPrice = supplierPrice + serviceFee;
    const customerPrice = supplierPrice + serviceFee + markUP;

    return {
      supplierPrice,
      serviceFee,
      markUP,
      totalPrice,
      customerPrice,
      applicantCount: visaRequest.noOfApplicants || 0,
    };
  };

  // Updated handleShowDocuments - now uses the documents from state
  const handleShowDocuments = () => {
    if (!visaRequest?.visaId) return;
    
    // If we already have documents from the Visas screen, just show the modal
    if (documents.length > 0) {
      setShowDocuments(true);
      return;
    }
    
    // If no documents were passed, try to get from localStorage as fallback
    const cachedDocuments = localStorage.getItem(`visa_documents_${visaRequest.visaId}`);
    if (cachedDocuments) {
      try {
        setDocuments(JSON.parse(cachedDocuments));
      } catch (e) {
        console.error("Error parsing cached documents:", e);
        setDocuments([]);
      }
    }
    
    setShowDocuments(true);
  };

  // Optional: Cache documents in localStorage when received
  useEffect(() => {
    if (documents.length > 0 && visaRequest?.visaId) {
      localStorage.setItem(`visa_documents_${visaRequest.visaId}`, JSON.stringify(documents));
    }
  }, [documents, visaRequest?.visaId]);

  // Razorpay integration
  function loadScript(src) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  async function displayRazorpay() {
    if (!visaRequest || !user) return;

    const pricing = calculatePricing();
    if (!pricing || pricing.totalPrice <= 0) {
      toast.error("Invalid amount for payment");
      return;
    }

    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    try {
      const result = await apiService.post(
        `${config.MAKEPAYMENT}?Amount=${pricing.totalPrice}&PayRequest=RAZORPAY&CompanyId=${user?.cid}`
      );

      if (!result.succeeded) {
        toast.error("Payment initiation failed. Try again.");
        return;
      }

      const { amount, id: order_id, currency, razorpayKey, name } = result.data;

      const options = {
        key: razorpayKey,
        amount: amount,
        currency: currency,
        name: name,
        description: `Visa Payment for ${visaRequest.visaName} - ${visaRequest.noOfApplicants} applicant(s)`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Prepare the complete update data with all pricing information
            const updateData = {
              visaRequestId: visaRequest.visaRequestId,
              visaId: visaRequest.visaId,
              userId: visaRequest.userId,
              companyId: visaRequest.companyId,
              leadSource: visaRequest.leadSource,
              visaReferenceNo: visaRequest.visaReferenceNo,
              visaName: visaRequest.visaName,
              countryName: visaRequest.countryName,
              applicantName: visaRequest.applicantName,
              supplierPrice: pricing.supplierPrice,
              gst: visaRequest.gst || 0,
              markUP: pricing.markUP,
              serviceFee: pricing.serviceFee, // Using the service fee from visa request
              customerPrice: pricing.customerPrice,
              totalPrice: pricing.totalPrice,
              visaRequestStatus: "PROCESSING",
              paymentStatus: "PAID",
              comments: visaRequest.comments,
              travelDate: visaRequest.travelDate,
              expectedDate: visaRequest.expectedDate,
              noOfApplicants: visaRequest.noOfApplicants,
              paymentGatewayNo: response.razorpay_payment_id,
              paymentGateway: "RAZORPAY",
              invoiceNo: visaRequest.invoiceNo,
            };

            // Update visa request with all pricing details
            const updateResponse = await apiService.put(
              `${config.UPDATEVISAREQUEST}/${visaRequest.visaRequestId}`,
              updateData
            );

            if (updateResponse && updateResponse.success) {
              VisaRequest();
            } else {
            }
          } catch (error) {
            console.error("Error updating status after payment:", error);
            toast.error(
              "Payment successful but status update failed. Please contact support."
            );
          }
        },
        prefill: {
          email: user?.email,
          contact: user?.phoneNumber,
        },
        notes: {
          address: "Smart Travel Visa Payment",
          visaRequestId: visaRequest.visaRequestId,
        },
        theme: {
          color: "#3399cc",
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Payment initiation failed. Please try again.");
    }
  }

  const handlePayment = async () => {
    if (!visaRequest) return;

    setIsProcessingPayment(true);
    try {
      await displayRazorpay();
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Payment failed. Please try again.");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div id="layout-wrapper">
        <Header />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "50vh" }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const pricing = calculatePricing();
  const isPaid = visaRequest?.paymentStatus === "PAID";

  return (
    <div id="layout-wrapper">
      <Header />
      <div className="vertical-overlay"></div>
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-light py-3">
                <div className="d-flex justify-content-between align-items-center w-100">
                  <h5 className="mb-0 bg-primary text-white p-2 rounded">
                    <RiVisaLine className="me-2" />
                    Visa Request Details
                  </h5>

                  <div>
                    <button
                      className="btn btn-success btn-sm me-2"
                      onClick={handleShowDocuments}
                    >
                      <RiFileList3Line className="me-1" />
                      Required Documents
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate("/client/visarequests")}
                    >
                      <RiArrowLeftLine className="me-1" />
                      Back
                    </button>
                  </div>
                </div>
              </div>

              {visaRequest && (
                <div className="card-body">
                  <div className="row mb-4">
                    <div className="col-md-8">
                      <h4 className="text-dark fw-bold mb-2">
                        {visaRequest.visaName} for {visaRequest.countryName}
                      </h4>
                    </div>
                  </div>

                  <div className="row">
                    {/* Left Column - Visa Details */}
                    <div className="col-lg-6 mb-4">
                      <div className="card bg-light border-0 h-100">
                        <div className="card-header bg-transparent border-0 py-2">
                          <h6 className="mb-0 text-primary">
                            <RiVisaLine className="me-2" />
                            Visa Information
                          </h6>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <DetailItem
                              icon={<RiFileList3Line />}
                              label="Reference"
                              value={visaRequest.visaReferenceNo}
                              className="col-6 mb-2"
                            />
                            <DetailItem
                              icon={<RiFileList3Line />}
                              label="Total Applicants"
                              value={visaRequest.noOfApplicants}
                              className="col-6 mb-2"
                            />
                            <DetailItem
                              icon={<RiFileList3Line />}
                              label="Invoice"
                              value={visaRequest.invoiceNo || "N/A"}
                              className="col-6 mb-2"
                            />
                          </div>

                          {/* Status Information Component */}
                          <StatusInformation visaRequest={visaRequest} />
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Pricing Information Component */}
                    <div className="col-lg-6 mb-4">
                      <PricingInformation
                        visaRequest={visaRequest}
                        pricing={pricing}
                        onPayment={handlePayment}
                        isProcessingPayment={isProcessingPayment}
                      />
                    </div>
                  </div>

                  {/* Applicants Information (Read Only) */}
                  <ApplicantsDetails
                    visaRequestId={visaRequestId}
                    visaRequest={visaRequest}
                    visaStatus={visaRequest.visaRequestStatus}
                    onApplicantsChange={VisaRequest}
                    readOnly={true}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Documents Modal */}
      {showDocuments && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowDocuments(false)}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header bg-primary text-white d-flex align-items-center">
                <h5 className="modal-title text-white mb-0 d-flex align-items-center">
                  <RiFileList3Line className="me-2" />
                  Document Requirements for {visaRequest?.visaName}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDocuments(false)}
                ></button>
              </div>

              <div className="modal-body">
                {documents.length > 0 ? (
                  <div className="list-group">
                    {documents.map((doc, index) => (
                      <div
                        key={index}
                        className="list-group-item border-0 py-3"
                      >
                        <div className="d-flex align-items-start">
                          <span className="badge bg-primary me-3 mt-1">
                            {index + 1}
                          </span>
                          <div className="flex-grow-1">
                            <h6 className="mb-1 text-dark">
                              {doc.documentName}
                            </h6>
                            {doc.description && (
                              <p className="text-muted mb-0 small">
                                {doc.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <RiFileList3Line className="display-4 text-muted mb-3" />
                    <h5 className="text-muted">
                      No document requirements found
                    </h5>
                    <p className="text-muted">
                      We couldn't find specific document requirements for this
                      visa type.
                    </p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setShowDocuments(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Status Information Component (Read Only)
const StatusInformation = ({ visaRequest }) => {
  return (
    <div className="col-12 mt-2 pt-2 border-top">
      <h6 className="mb-2 text-primary">Status Information</h6>

      <div className="row">
        <div className="col-6">
          <DetailItem
            icon={<RiFileList3Line />}
            label="Visa Status"
            value={visaRequest.visaRequestStatus}
            isStatus
            className="mb-1 small"
          />
        </div>

        <div className="col-6">
          <DetailItem
            icon={<RiFileList3Line />}
            label="Payment Status"
            value={visaRequest.paymentStatus}
            isPayment
            className="mb-1 small"
          />
        </div>
      </div>
    </div>
  );
};

const PricingInformation = ({
  visaRequest,
  pricing,
  onPayment,
  isProcessingPayment,
}) => {
  const isPaid = visaRequest?.paymentStatus === "PAID";

  return (
    <div className="card bg-light border-0 h-100">
      <div className="card-header bg-transparent border-0 py-2">
        <h6 className="mb-0 text-primary">Pricing Information (INR)</h6>
      </div>

      <div className="card-body p-3">
        {/* Warning message if no supplier */}
        {/* {(!visaRequest.supplierId ||
          visaRequest.supplierId ===
            "00000000-0000-0000-0000-000000000000") && (
          <div className="text-center p-2 mb-2">
            <div className="alert alert-warning small mb-0">
              No supplier assigned
            </div>
          </div>
        )} */}

        {/* Price Breakdown */}
        <div className="col-12">
          <div className="bg-white p-3 rounded border small">
            <div className="row g-1">
              <div className="col-12 fw-semibold text-center mb-2">
                Price Breakdown (INR)
              </div>

              {/* 1. Applicant Price */}
              <div className="col-12 mb-2 d-flex justify-content-between px-2">
                <span>
                  Applicant price for {pricing?.applicantCount || 1}{" "}
                  {pricing?.applicantCount === 1 ? "person" : "persons"}:
                </span>
                <span>
                  INR {(pricing?.supplierPrice || 0).toLocaleString("en-IN")}
                </span>
              </div>

              {/* 2. Service Fee - Now using the service fee from visa request */}
              <div className="col-12 mb-2 d-flex justify-content-between px-2">
                <span>
                  Service fee ({pricing?.applicantCount || 1} applicant
                  {pricing?.applicantCount !== 1 ? "s" : ""} × INR{" "}
                  {pricing?.serviceFee ? (pricing.serviceFee / pricing.applicantCount).toFixed(0) : 0}):
                </span>
                <span className="text-info">
                  INR {(pricing?.serviceFee || 0).toLocaleString("en-IN")}
                </span>
              </div>

              {/* 3. Total Price + Pay Button */}
              <div className="col-12 mb-2 d-flex justify-content-between align-items-center px-2 border-top pt-2">
                <span className="fw-semibold">Total Price:</span>

                <div className="d-flex align-items-center gap-2">
                  <span className="fw-bold text-primary">
                    INR {(pricing?.totalPrice || 0).toLocaleString("en-IN")}
                  </span>

                  {!isPaid ? (
                    <button
                      className="btn btn-success btn-sm"
                      style={{ padding: "2px 8px" }}
                      onClick={onPayment}
                      disabled={
                        isProcessingPayment ||
                        !pricing ||
                        pricing.totalPrice <= 0
                      }
                    >
                      {isProcessingPayment ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" />
                          ...
                        </>
                      ) : (
                        "Pay Now"
                      )}
                    </button>
                  ) : (
                    <span className="badge bg-success">Paid</span>
                  )}
                </div>
              </div>

              {/* 4. Markup */}
              <div className="col-12 mb-2 d-flex justify-content-between px-2">
                <span>Markup</span>
                <span className="text-success">
                  + INR {(pricing?.markUP || 0).toLocaleString("en-IN")}
                </span>
              </div>

              {/* 5. Customer Price */}
              <div className="col-12 mb-2 d-flex justify-content-between px-2 border-top pt-2">
                <span className="fw-semibold">Customer Price:</span>
                <span className="fw-semibold text-dark">
                  INR {(pricing?.customerPrice || 0).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// DetailItem component
const DetailItem = ({
  icon,
  label,
  value,
  isStatus,
  isPayment,
  className = "",
  small = false,
}) => {
  let valueElement = value;

  if (isStatus) {
    valueElement = (
      <span className={`badge ${getStatusBadgeClass(value)}`}>{value}</span>
    );
  } else if (isPayment) {
    valueElement = (
      <span className={`badge ${getPaymentBadgeClass(value)}`}>{value}</span>
    );
  }

  const textSizeClass = small ? "small" : "";

  return (
    <div className={`detail-item ${className}`}>
      <div className="d-flex align-items-center">
        <span
          className={`text-primary me-1 ${textSizeClass}`}
          style={{ minWidth: "16px" }}
        >
          {icon}
        </span>
        <span className={`text-muted me-1 ${textSizeClass}`}>{label}:</span>
        <span className={`fw-medium text-dark text-break ${textSizeClass}`}>
          {valueElement}
        </span>
      </div>
    </div>
  );
};

const getStatusBadgeClass = (status) => {
  switch (status?.toUpperCase()) {
    case "APPROVED":
      return "bg-success";
    case "PENDING":
    case "PROCESSING":
    case "DOCUMENT_PENDING":
      return "bg-warning text-dark";
    case "REJECTED":
      return "bg-danger";
    case "SUBMITTED":
    case "NEW":
      return "bg-info";
    case "DOCUMENTS_MISSING":
      return "bg-danger";
    default:
      return "bg-secondary";
  }
};

const getPaymentBadgeClass = (status) => {
  switch (status?.toUpperCase()) {
    case "PAID":
      return "bg-success";
    case "PENDING":
      return "bg-warning text-dark";
    case "FAILED":
    case "CANCELLED":
      return "bg-danger";
    case "REFUNDED":
      return "bg-info";
    default:
      return "bg-secondary";
  }
};

export default RequestDetails;